import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Alert, Spinner, Table, Input } from 'reactstrap';
// Removed useAuth import - not needed for external form
import offersService from '../../service/offersService.ts';
// import dealService from '../../service/dealService.ts';
import extDealService from '../../service/extDealService.ts';
import extDocumentService from '../../service/extDocumentService.ts';
import { formatNumber } from '../../utils/formatUtils';
import { OFFER_CONFIG } from '../../config/offerConfig';

const OffersSelect = () => {
  // Extraer token del fragmento de la URL y limpiar la URL
  useEffect(() => {
    const extractTokenFromFragment = () => {
      const hash = window.location.hash;
      console.log('🔍 Current hash:', hash);
      const tokenMatch = hash.match(/#t=([^&]+)/);
      if (tokenMatch) {
        const token = tokenMatch[1];
        console.log('🔑 Magic token found in URL:', token);
        setMagicToken(token);
        // Limpiar la URL eliminando el fragmento
        if (window.history.replaceState) {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
          console.log('🧹 URL cleaned, token stored in memory');
        }
      } else {
        console.log('❌ No magic token found in URL fragment');
      }
      setUrlCleaned(true);
    };
    extractTokenFromFragment();
  }, []);
  // Estado para el flujo de pasos (selección de oferta, blank check, etc.)
  const [pageStep, setPageStep] = useState(1); // 1: selección de oferta, 2: blank check
  // Estados y refs faltantes para funcionamiento correcto
  const [offers, setOffers] = useState([]);
  const [hasLoadedOffers, setHasLoadedOffers] = useState(false);
  const [editingAmounts, setEditingAmounts] = useState({});
  const [error, setError] = useState(null);
  const [dealId, setDealId] = useState(null);
  const [dealPublicId, setDealPublicId] = useState(null);
  const [offersSummary, setOffersSummary] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const isLoadingRef = useRef(false);
  // Removed useAuth - external form uses magic token authentication
  const [magicToken, setMagicToken] = useState(null);
  const [accessToken, setAccessToken] = useState(null); // token especial para endpoints
  const [sessionId, setSessionId] = useState(null);     // session especial para endpoints
  const [tokenExpiry, setTokenExpiry] = useState(null); // para controlar expiración del token
  const [urlCleaned, setUrlCleaned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exchangeLoading, setExchangeLoading] = useState(false);
  const [selectLoading, setSelectLoading] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [addendumChecked, setAddendumChecked] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedOfferData, setSelectedOfferData] = useState(null);
  const [blankCheckFile, setBlankCheckFile] = useState(null);
  const [uploadingBlankCheck, setUploadingBlankCheck] = useState(false);
  const [blankCheckUploadError, setBlankCheckUploadError] = useState(null);

  // Timer para token expiry
  useEffect(() => {
    if (!tokenExpiry) return;

    const updateTimer = () => {
      const now = new Date();
      const timeLeft = tokenExpiry.getTime() - now.getTime();
      if (timeLeft <= 0) {
        setTimeRemaining(null);
        setError('Session expired. Please refresh the page and try again.');
        return;
      }
      const minutes = Math.floor(timeLeft / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      setTimeRemaining({ minutes, seconds });
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [tokenExpiry]);

  // Load offers when magic token is available and URL is cleaned
  useEffect(() => {
    if (magicToken && urlCleaned && !hasLoadedOffers) {
      exchangeMagicTokenAndLoadOffers();
    }
  }, [magicToken, urlCleaned, hasLoadedOffers]);

  // Exchange magic token for access token and session, then load offers
  const exchangeMagicTokenAndLoadOffers = async () => {
    if (!magicToken || isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setExchangeLoading(true);
    setError(null);
    
    try {
      // Step 1: Exchange magic token
      console.log('🔄 OffersSelect - Exchanging magic token...');
      const exchangeResponse = await offersService.exchangeMagicToken(magicToken);
      console.log('🔄 OffersSelect - Exchange response full:', exchangeResponse.data);
      
      if (exchangeResponse.data) {
        const { accessToken, sessionId, offersSummary, deadlineUtc } = exchangeResponse.data;
        
        // Store session data in memory
        setAccessToken(accessToken);
        setSessionId(sessionId);
        setOffersSummary(offersSummary);
        setDeadline(deadlineUtc);
        
        // Calculate token expiry time (current time + expiresIn seconds)
        const expiryTime = new Date(Date.now() + (exchangeResponse.data.expiresIn * 1000));
        setTokenExpiry(expiryTime);
        
        // Step 2: Load offers using the access token and session
        await loadOffers(accessToken, sessionId);
      }
    } catch (error) {
      console.error('❌ Error exchanging magic token:', error);
      setError('Failed to validate access link. Please try again or contact support.');
    } finally {
      setExchangeLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Load offers using access token and session
  const loadOffers = async (token, session) => {
    if (!token || !session) {
      return;
    }

    setLoading(true);
    
    try {
      console.log('🔄 OffersSelect - Getting deals by link...');
      const offersResponse = await offersService.getDealsByLink(token, session);
      console.log('🔄 OffersSelect - getDealsByLink full response:', offersResponse);
      
      if (offersResponse.data && Array.isArray(offersResponse.data)) {
        
        // Debug: Log the actual response structure
        console.log('OffersSelect - Raw API Response:', offersResponse.data);
        console.log('OffersSelect - First offer fields:', offersResponse.data[0] ? Object.keys(offersResponse.data[0]) : 'No offers');
        console.log('OffersSelect - First offer full object:', offersResponse.data[0]);
        
        // Extract dealId from the first offer and get dealPublicId from deal service
        if (offersResponse.data.length > 0) {
          if (offersResponse.data[0].dealId) {
            const dealPrivateId = offersResponse.data[0].dealId;
            setDealId(dealPrivateId);
            console.log('OffersSelect - Deal Private ID extracted:', dealPrivateId);
            console.log('OffersSelect - Deal Private ID type:', typeof dealPrivateId);
            
            // Get dealPublicId from deal service
            try {
              console.log('🔄 OffersSelect - Getting deal details to extract public ID...');
              const dealResponse = await extDealService.getDealByPrivateIdExt(dealPrivateId);
              if (dealResponse.data && dealResponse.data.publicId) {
                setDealPublicId(dealResponse.data.publicId);
                console.log('✅ OffersSelect - Deal Public ID extracted:', dealResponse.data.publicId);
              } else {
                console.warn('⚠️ OffersSelect - No publicId found in deal response');
              }
            } catch (dealError) {
              console.error('❌ OffersSelect - Error getting deal details:', dealError);
            }
          } else {
            console.warn('⚠️ OffersSelect - No dealId (private) found in offers response');
          }
        } else {
          console.warn('⚠️ OffersSelect - No offers found in response');
        }
        
        // Simply use the offers as they come from the API - no filtering needed
        // since the API is already returning unique offers
        const cleanedOffers = offersResponse.data.map(offer => ({
          ...offer,
          // Ensure all required fields exist
          id: offer.id,
          term_duration_months: offer.term_duration_months || 0,
          advance_amount: offer.advance_amount || 0,
          net_payback: offer.net_payback || 0,
          factor: offer.factor || 0,
          monthly_payment: offer.monthly_payment || 0,
          processing_fee: offer.processing_fee || 0,
        }));
        
        // Set offers in state
        setOffers(cleanedOffers);
        setHasLoadedOffers(true);
        
        // Initialize editing amounts with original gross advance amounts
        const initialAmounts = {};
        cleanedOffers.forEach(offer => {
          // Use gross_advance_amount if available, otherwise calculate it
          const originalGrossAmount = offer.gross_advance_amount || 
                                     offer.grossAdvanceAmount || 
                                     (Number(offer.advance_amount || 0) + Number(offer.processing_fee || 0));
          if (originalGrossAmount > 0) {
            initialAmounts[offer.id] = originalGrossAmount;
          }
        });
        setEditingAmounts(initialAmounts);
        
      } else {
        setError('Invalid response from server');
      }
    } catch (error) {
      console.error('❌ Error loading offers:', error);
      setError('Failed to load offers. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Handle amount adjustment
  const handleAmountChange = (offerId, newAmount) => {
    setEditingAmounts(prev => ({
      ...prev,
      [offerId]: newAmount
    }));
  };

  // Function to send contract communication after offer selection
  const sendContractCommunication = async (token, dealPrivateId) => {
    console.log('🚀🚀🚀 CONTRACT COMMUNICATION FUNCTION CALLED 🚀🚀🚀');
    console.log('🚀 sendContractCommunication called with:', {
      hasToken: !!token,
      dealPrivateId: dealPrivateId,
      tokenType: typeof token,
      dealIdType: typeof dealPrivateId
    });
    
    if (!dealPrivateId) {
      console.error('❌ No dealPrivateId provided for contract communication');
      throw new Error('Deal Private ID is required for contract communication');
    }
    
    try {
      console.log('📄 Getting funding document template...');
      
      // Step 1: Get the funding document template (EXT)
      const templateResponse = await extDealService.getTemplateByActionExt('FUNDING_DOCUMENT');
      const templateArray = templateResponse.data;
      
      if (!templateArray || !Array.isArray(templateArray) || templateArray.length === 0) {
        console.error('❌ Template validation failed:', {
          templateArray: templateArray,
          isArray: Array.isArray(templateArray),
          length: templateArray?.length
        });
        throw new Error('FUNDING_DOCUMENT template not found');
      }
      
      const template = templateArray[0]; // Get the first template from the array
      console.log('📄 Template obtained:', template);
      
      // Step 2: Get deal data by private ID (EXT)
      const dealResponse = await extDealService.getDealByPrivateIdExt(dealPrivateId);
      const dealData = dealResponse.data;
      
      // Step 3: Get selection status (EXT)
      const selectionResponse = await extDealService.getSelectionStatusExt(dealPrivateId);
      const selectionData = selectionResponse.data;
      
      console.log('📊 Deal data:', dealData);
      console.log('📊 Selection data:', selectionData);
      
      // Step 4: Get selected offer details if available (EXT)
      let selectedOfferData = null;
      if (selectionData.selectedOfferId) {
        try {
          const offerResponse = await extDealService.getDealOfferByIdExt(dealPrivateId, selectionData.selectedOfferId);
          selectedOfferData = offerResponse.data;
          console.log('📊 Selected offer data:', selectedOfferData);
        } catch (offerError) {
          console.warn('Could not get selected offer details:', offerError);
        }
      }
      
      // Step 5: Get owner details for template values
      const primaryOwner = dealData.owners && dealData.owners.length > 0 ? dealData.owners[0] : null;
      const secondaryOwner = dealData.owners && dealData.owners.length > 1 ? dealData.owners[1] : null;
      
      const primaryEmail = primaryOwner ? getPrimaryEmailContact(primaryOwner) : null;
      const primaryPhone = primaryOwner ? getPrimaryPhoneContact(primaryOwner) : null;
      
      // Step 6: Get address details from primary owner's addresses array
      const physicalAddress = primaryOwner && primaryOwner.addresses && Array.isArray(primaryOwner.addresses) 
        ? primaryOwner.addresses.find(addr => addr.addressType === 'PHYSICAL' && !addr.deleted)
        : null;
      
      // Step 7: Process the template with all available data
      const processedTemplate = {
        id: String(template.id || '').trim(),
        name: String(template.name || '').trim(),
        channel: String(template.channel || '').trim(),
        templateType: String(template.templateType || '').trim(),
        subject: String(template.subject || '').trim(),
        body: String(template.body || '').trim(),
        values: {
          // Deal basic info
          referenceId: String(dealData.publicId || '').trim(),
          legal_name: String(dealData.legalName || '').trim(),
          dba_name: String(dealData.dbaName || '').trim(),
          business_structure: String(dealData.businessStructure || '').trim(),
          state_of_incorporation: String(dealData.stateOfInc || '').trim(),
          
          // Owner info
          first_name: primaryOwner ? String(primaryOwner.firstName || primaryOwner.first_name || '').trim() : '',
          last_name: primaryOwner ? String(primaryOwner.lastName || primaryOwner.last_name || '').trim() : '',
          owner2_full_name: secondaryOwner ? String(getOwnerFullName(secondaryOwner) || '').trim() : '',
          
          // Contact info
          email_1: primaryEmail ? String(primaryEmail.value || '').trim() : '',
          mobile_phone: primaryPhone ? String(primaryPhone.value || '').trim() : '',
          
          // Address info from addresses array
          address: physicalAddress ? String(physicalAddress.street1 || '').trim() : '',
          city: physicalAddress ? String(physicalAddress.city || '').trim() : '',
          state: physicalAddress ? String(physicalAddress.state || '').trim() : '',
          zip: physicalAddress ? String(physicalAddress.zipCode || '').trim() : '',
          
          // Financial info from selected offer
          advance_amount: selectedOfferData ? String(selectedOfferData.advance_amount || 0).trim() : '0',
          net_funded_amount: selectedOfferData ? String(selectedOfferData.net_payback || selectedOfferData.advance_amount || 0).trim() : '0',
          daily_ach_amount: selectedOfferData ? String(selectedOfferData.monthly_payment || 0).trim() : '0',
          origination_fee: selectedOfferData ? String(selectedOfferData.processing_fee || 0).trim() : '0',
          gross_percentage: selectedOfferData ? String(selectedOfferData.factor || 0).trim() : '0',
          
          // Additional fields that might be needed
          sum_of_deductions: '0',
          combined_balance: '0',
          third_party_amount: '0'
        }
      };
      
      console.log('📄 Processed template:', processedTemplate);
      
      // Step 8: Prepare contacts from deal owners
      const contacts = [];
      
      if (dealData.owners && Array.isArray(dealData.owners)) {
        for (const owner of dealData.owners) {
          const ownerEmail = getPrimaryEmailContact(owner);
          
          if (ownerEmail) {
            const ownerFullName = getOwnerFullName(owner);
            const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
            
            contacts.push({
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              email: String(ownerEmail.value).trim(),
              channels: ["EMAIL"]
            });
          }
        }
      }
      
      if (contacts.length === 0) {
        throw new Error('No valid email contacts found for contract communication');
      }
      
      // Step 9: Generate contract request ID
      const contractRequestId = `contract-${Date.now()}-${dealData.publicId}`;
      
      // Step 10: Prepare and send communication payload
      const communicationPayload = {
        requestId: String(contractRequestId).trim(),
        action: "CONTRACT",
        dealId: dealPrivateId,
        contacts: contacts,
        templates: [processedTemplate]
      };
      
      console.log('📧 Contract communication payload:', JSON.stringify(communicationPayload, null, 2));
      
      // Step 11: Send the communication (EXT)
      const dispatchResponse = await extDealService.dispatchCommunicationExt(communicationPayload);
      console.log('📤 Dispatch response received:', dispatchResponse);
      
      console.log('✅ Contract communication sent successfully');
      
    } catch (error) {
      console.error('❌❌❌ SENDCONTRACTCOMMUNICATION ERROR ❌❌❌');
      console.error('❌ Error in contract communication:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers
      });
      
      // Force alert for production debugging
      alert(`sendContractCommunication error: ${error.message} - Status: ${error.response?.status}`);
      
      throw error;
    }
  };

  // Helper function to get token
  const getToken = () => {
    return accessToken;
  };

  // Function to update offer if modified (similar to modal logic)
  const updateOfferIfModified = async (offerId, offer) => {
    try {
      // Get original and current gross amounts
      const fee = Number(offer.processing_fee || 0);
      const originalGrossAmount = offer.gross_advance_amount || 
                                 offer.grossAdvanceAmount || 
                                 (Number(offer.advance_amount || 0) + fee);
      const currentGross = Number(editingAmounts[offerId] || originalGrossAmount);
      
      // Check if the offer was modified
      const wasModified = currentGross !== originalGrossAmount;
      
      if (!wasModified) {
        console.log('✅ Offer was not modified, using original offer ID:', offerId);
        return { offerId, backendOfferId: offer.originalId || offerId, wasUpdated: false };
      }

      console.log('📝 Offer was modified, creating backup and updating with calculated values...');
      
      if (!dealId) {
        throw new Error('Deal Private ID not available');
      }

      // Calculate all fields based on current gross amount
      const factor = Number(offer.factor || 0);
      const termDuration = offer.term_duration_months || 1;
      const currentAdvance = Math.max(0, currentGross - fee);
      const netPayback = currentGross * factor;
      const monthlyPayment = netPayback / termDuration;

      // Step 1: Create a backup offer with the ORIGINAL values before updating
      console.log('💾 Creating backup offer with original values...');
      const backupOfferData = {
        term_duration_months: offer.term_duration_months || offer.term,
        gross_payback: offer.gross_payback || offer.payback,
        gross_advance_amount: originalGrossAmount,
        net_payback: offer.net_payback || offer.netAdvanceAmount,
        monthly_payment: offer.monthly_payment || offer.monthlyGrossMargin,
        advance_amount: offer.advance_amount || offer.advanceAmount,
        factor: offer.factor,
        processing_fee: offer.processing_fee || 0,
        note: `Backup of original offer values before modification (Original ID: ${offer.originalId || offerId})`,
      };

      console.log('💾 Backup offer data:', backupOfferData);

      try {
        const backupResponse = await extDealService.createDealOfferExt(
          dealId, 
          backupOfferData,
          { 
            idempotencyKey: `backup-${Date.now()}-${offerId}`
          }
        );
        
        if (backupResponse.data && backupResponse.data.id) {
          console.log('✅ Backup offer created successfully with ID:', backupResponse.data.id);
        } else {
          console.warn('⚠️ Backup offer creation returned no ID, continuing anyway...');
        }
      } catch (backupError) {
        console.error('❌ Error creating backup offer:', backupError);
        console.warn('⚠️ Continuing with update despite backup error...');
        // Continue with the update even if backup fails
      }

      // Step 2: Prepare the updated offer data with the NEW calculated values
      const updatedOfferData = {
        term_duration_months: termDuration,
        gross_payback: netPayback, // This might need to be different, check with backend
        gross_advance_amount: currentGross,
        net_payback: netPayback,
        monthly_payment: monthlyPayment,
        advance_amount: currentAdvance,
        factor: factor,
        processing_fee: fee,
        note: 'Modified offer updated from external form selection',
      };

      console.log('📝 Updating original offer with new data:', updatedOfferData);

      // Step 3: Update the existing offer with the new values
      const backendOfferId = offer.originalId || offerId;
      const response = await extDealService.updateDealOfferExt(
        dealId,
        backendOfferId,
        updatedOfferData,
        {
          idempotencyKey: `update-${Date.now()}-${backendOfferId}`
        }
      );
      
      if (!response.data) {
        throw new Error('Failed to update offer');
      }

      console.log('✅ Original offer updated successfully:', response.data);

      return { offerId: backendOfferId, backendOfferId: backendOfferId, wasUpdated: true };
    } catch (error) {
      console.error('❌ Error updating offer:', error);
      throw error;
    }
  };

  // Helper functions for template processing
  const getOwnerFullName = (owner) => {
    const firstName = owner.firstName || owner.first_name || '';
    const middleName = owner.middleName || owner.middle_name || '';
    const lastName = owner.lastName || owner.last_name || '';
    
    const fullName = [firstName, middleName, lastName]
      .filter(name => name && name.trim() !== '')
      .join(' ');
    
    return fullName || 'Unknown Owner';
  };

  const getPrimaryEmailContact = (owner) => {
    let contacts = owner.contacts || owner.contacs || [];
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'EMAIL'
    );
  };

  const getPrimaryPhoneContact = (owner) => {
    let contacts = owner.contacts || owner.contacs || [];
    
    if (!Array.isArray(contacts)) {
      return null;
    }

    return contacts.find(contact => 
      contact.isPrimary === true && 
      contact.deleted === false && 
      contact.type === 'PHONE'
    );
  };

  // Función para manejar el flujo de Addendum
  const handleAddendumFlow = async (offerId, token, dealPrivateId) => {
    try {
      if (!token) {
        throw new Error('Authentication token not available');
      }

      if (!dealPrivateId) {
        console.error('❌ No dealPrivateId provided for addendum communication');
        throw new Error('Deal Private ID is required for addendum communication');
      }

      console.log('📄 Getting ADDENDUM_STATE1 template...');
      
      // Step 1: Get the addendum template (EXT)
      const templateResponse = await extDealService.getTemplateByActionExt('ADDENDUM_STATE1');
      const templates = templateResponse.data;
      if (!templates || templates.length === 0) {
        throw new Error('No addendum template found');
      }
      
      const template = templates[0]; // Use the first template
      console.log('📄 Addendum template received:', template);

      // Step 2: Get the selected offer data and get current calculated values
      const selectedOfferData = offers.find(o => o.id === offerId);
      if (!selectedOfferData) {
        throw new Error('Selected offer not found');
      }

      // Get current edited gross amount for the addendum
      const fee = Number(selectedOfferData.processing_fee || 0);
      const originalGrossAmount = selectedOfferData.gross_advance_amount || 
                                 selectedOfferData.grossAdvanceAmount || 
                                 (Number(selectedOfferData.advance_amount || 0) + fee);
      const currentGross = Number(editingAmounts[offerId] || originalGrossAmount);
      const currentAdvance = Math.max(0, currentGross - fee);

      // Step 3: Get deal data by private ID for additional info (EXT)
      const dealResponse = await extDealService.getDealByPrivateIdExt(dealPrivateId);
      const dealData = dealResponse.data;

      // Step 4: Process the template with deal data (use CURRENT EDITED values)
      const processedTemplate = {
        id: String(template.id || '').trim() + '.docx',
        name: String(template.name || '').trim(),
        channel: String(template.channel || 'EMAIL').trim(),
        templateType: String(template.templateType || 'HTML').trim(),
        subject: template.subject ? String(template.subject).trim() : null,
        body: String(template.body || '').trim(),
        values: {
          referenceId: String(dealData?.publicId || dealData?.id || '').trim(),
          term: String(selectedOfferData.term_duration_months || selectedOfferData.term || 0).trim(),
          factor_rate: String(selectedOfferData.factor || 0).trim(),
          advance_amount: String(currentAdvance).trim(), // Use calculated current advance amount
          legal_name: String(dealData?.legalName || dealData?.company_name || '').trim(),
          dba_name: String(dealData?.dbaName || dealData?.dba_name || dealData?.companyName || '').trim(),
          contract_date: new Date().toISOString().split('T')[0] // Format: YYYY-MM-DD
        }
      };

      console.log('📄 Processed addendum template:', processedTemplate);

      // Step 5: Prepare contacts from deal owners
      const contacts = [];
      
      if (dealData?.owners && Array.isArray(dealData.owners)) {
        for (const owner of dealData.owners) {
          const ownerEmail = getPrimaryEmailContact(owner);
          
          if (ownerEmail) {
            const ownerFullName = getOwnerFullName(owner);
            const ownerId = owner.id || owner.ownerId || owner.owner_id || `owner-${Date.now()}`;
            
            contacts.push({
              id: String(ownerId).trim(),
              name: String(ownerFullName).trim(),
              email: String(ownerEmail.value).trim(),
              phone: owner.phone || '',
              channels: ["EMAIL"]
            });
          }
        }
      }
      
      if (contacts.length === 0) {
        throw new Error('No valid email contacts found for addendum communication');
      }

      // Step 6: Generate addendum request ID
      const addendumRequestId = `addendum-${Date.now()}-${dealData?.publicId || dealData?.id || 'unknown'}`;

      // Step 7: Prepare and send communication payload (EXT)
      const communicationPayload = {
        requestId: String(addendumRequestId).trim(),
        action: "ADDENDUM",
        dealId: dealPrivateId,
        contacts: contacts,
        templates: [processedTemplate]
      };

      console.log('📧 Addendum communication payload:', JSON.stringify(communicationPayload, null, 2));

      // Step 8: Send the addendum communication (EXT)
      await extDealService.dispatchCommunicationExt(communicationPayload);
      
      console.log('✅ Addendum communication sent successfully');
      
      // Send deal-addendum request for addendum flow (EXT)
      console.log('🔄 Sending deal-addendum request for addendum flow...');
      try {
        await extDealService.createDealAddendumExt({
          deal_id: dealPrivateId,
          has_addendum: true // Addendum checkbox is active
        });
        console.log('✅ Deal-addendum request sent successfully for addendum flow');
      } catch (addendumError) {
        console.error('❌ Error sending deal-addendum request for addendum flow:', addendumError);
        // Don't fail the whole process if this request fails
      }
      
    } catch (error) {
      console.error('❌ Error in addendum flow:', error);
      throw error;
    }
  };

  // Handle offer selection
  const handleSelectOffer = async (offerId) => {
    console.log('🟢🟢🟢 ===== handleSelectOffer CALLED ===== 🟢🟢🟢');
    console.log('🔵 handleSelectOffer called with offerId:', offerId);
    console.log('🔍 Current session data:', {
      hasAccessToken: !!accessToken,
      hasSessionId: !!sessionId,
      accessToken: accessToken ? 'Present' : 'Missing',
      sessionId: sessionId ? 'Present' : 'Missing',
      dealId: dealId || 'Not available',
      dealPublicId: dealPublicId || 'Not available'
    });
    
    // Check if any offer is already being selected
    if (Object.values(selectLoading).some(loading => loading)) {
      console.warn('⚠️ An offer is already being selected, ignoring this click');
      return;
    }
    
    if (!accessToken || !sessionId) {
      console.error('❌ Session data missing - accessToken:', !!accessToken, 'sessionId:', !!sessionId);
      setError('Session expired. Please refresh the page and try again.');
      return;
    }

    // Find the offer to get the default amount
    const offer = offers.find(o => o.id === offerId);
    if (!offer) {
      setError('Offer not found.');
      return;
    }

    // Get the original gross amount
    const fee = Number(offer.processing_fee || 0);
    const originalGrossAmount = offer.gross_advance_amount || 
                               offer.grossAdvanceAmount || 
                               (Number(offer.advance_amount || 0) + fee);
    
    // Get current edited gross amount
    const currentGross = Number(editingAmounts[offerId] || originalGrossAmount);
    
    // Calculate advance amount from edited gross amount
    const amount = Math.max(0, currentGross - fee); // advance = gross - fee
    
    if (!amount || amount <= 0) {
      setError('Please specify a valid amount for the selected offer.');
      return;
    }

    console.log(`🎯 Selecting offer ${offerId} with calculated amount ${amount}`);
    console.log('🔍 Original gross amount:', originalGrossAmount);
    console.log('🔍 Current edited gross amount:', currentGross);
    console.log('🔍 Processing fee:', fee);
    console.log('🔍 Calculated advance amount:', amount);
    console.log('🔍 Original offer advance_amount:', offer.advance_amount);
    console.log('🔍 Offer was modified:', currentGross !== originalGrossAmount);
    console.log('🔍 Deal Private ID:', dealId);
    console.log('🔍 Deal Public ID:', dealPublicId);
    
    if (!dealId) {
      console.error('❌ Missing deal Private ID - dealId:', dealId);
      setError('Deal information is missing. Please refresh the page and try again.');
      return;
    }
    
    // Log warning if dealPublicId is missing but continue anyway
    if (!dealPublicId) {
      console.warn('⚠️ Deal Public ID is null - will attempt selection with only Private ID');
    }
    
    setSelectLoading(prev => ({ ...prev, [offerId]: true }));
    setError(null);

    try {
      // Update offer if modified, then select it
      console.log('🔄 Checking if offer needs to be updated before selection...');
      const { offerId: finalOfferId, backendOfferId, wasUpdated } = await updateOfferIfModified(offerId, offer);
      
      if (wasUpdated) {
        console.log('✅ Offer updated, now selecting with backend ID:', backendOfferId);
      } else {
        console.log('✅ Offer not modified, selecting with original ID:', backendOfferId);
      }

      // Now select the offer using the backend offer ID and the calculated amount
      const response = await offersService.selectOffer(backendOfferId, amount, accessToken, sessionId, dealId, dealPublicId);
      
      if (response.data) {
        console.log('✅ Offer selected successfully:', response.data);
        
        // Update deal status to CONTRACT after successful offer selection
        if (dealId) {
          console.log('🔄 Updating deal status to CONTRACT after offer selection...');
          if (accessToken) {
            // Update deal status and substatus using the access token from magic token exchange
            try{
              await extDealService.updateDealStatusExt(dealId, 'CONTRACT');
              console.log('✅ Deal status updated to CONTRACT successfully');
              
              await extDealService.updateDealSubstatusExt(dealId, 'CONTRACT_SENT');
              console.log('✅ Deal substatus updated to CONTRACT_SENT successfully');
            } catch (statusError) {
              console.error('❌ Error updating deal status/substatus to CONTRACT:', statusError);
              // Don't fail the whole process if status update fails
              // The offer selection was successful, just log the error
            }

            // Send communication after successful offer selection
            // Use accessToken (from magic token exchange) for all API calls
            console.log('🔄 About to start communication flow with:', {
              hasAccessToken: !!accessToken,
              dealId: dealId,
              accessTokenLength: accessToken ? accessToken.length : 0,
              addendumChecked: addendumChecked
            });
            
            if (!dealId) {
              console.error('❌ No dealId available for communication flow');
              alert('Error: Deal ID is not available. Cannot send communications.');
              return;
            }
            
            if (addendumChecked) {
              // Execute addendum flow (use backendOfferId which has the updated values)
              try {
                console.log('🔄 Addendum checkbox is checked, executing addendum flow...');
                await handleAddendumFlow(backendOfferId, accessToken, dealId);
                console.log('✅ Addendum communication sent successfully');
                
                // Send deal-addendum request for addendum flow (EXT)
                console.log('🔄 Sending deal-addendum request for addendum flow...');
                try {
                  await extDealService.createDealAddendumExt({
                    deal_id: dealId,
                    has_addendum: true // Addendum checkbox is active
                  });
                  console.log('✅ Deal-addendum request sent successfully for addendum flow');
                } catch (addendumError) {
                  console.error('❌ Error sending deal-addendum request for addendum flow:', addendumError);
                  // Don't fail the whole process if this request fails
                }
              } catch (addendumError) {
                console.error('❌❌❌ ADDENDUM FLOW ERROR ❌❌❌');
                console.error('❌ Error in addendum flow:', addendumError);
                alert(`Addendum flow error: ${addendumError.message}`);
              }
            } else {
              // Execute normal contract flow
              try {
                console.log('🔄🔄🔄 ABOUT TO CALL CONTRACT COMMUNICATION 🔄🔄🔄');
                console.log('🔄 Environment:', process.env.NODE_ENV);
                console.log('🔄 Base URL:', process.env.NODE_ENV === 'development' ? 'local' : 'production');
                console.log('🔄 Access Token length:', accessToken?.length);
                console.log('🔄 DealId:', dealId);
                console.log('🔄 Starting contract communication flow...');
                
                await sendContractCommunication(accessToken, dealId);
                
                console.log('✅✅✅ CONTRACT COMMUNICATION COMPLETED ✅✅✅');
                console.log('✅ Contract communication sent successfully');
                
                // Send deal-addendum request for contract flow (EXT)
                console.log('🔄 Sending deal-addendum request for contract flow...');
                try {
                  await extDealService.createDealAddendumExt({
                    deal_id: dealId,
                    has_addendum: false // Addendum checkbox is NOT active
                  });
                  console.log('✅ Deal-addendum request sent successfully for contract flow');
                } catch (addendumError) {
                  console.error('❌ Error sending deal-addendum request for contract flow:', addendumError);
                  // Don't fail the whole process if this request fails
                }
              } catch (contractError) {
                console.error('❌❌❌ CONTRACT FLOW ERROR ❌❌❌');
                console.error('❌ Error in contract flow:', contractError);
                console.error('❌ Contract error details:', {
                  message: contractError.message,
                  stack: contractError.stack,
                  response: contractError.response?.data,
                  status: contractError.response?.status,
                  statusText: contractError.response?.statusText,
                  url: contractError.config?.url,
                  method: contractError.config?.method
                });
                
                // Force alert to make sure error is visible in production
                alert(`Contract flow error: ${contractError.message}`);
              }
            }
          } else {
            console.warn('⚠️ No authentication token available for status update');
          }
        } else {
          console.warn('⚠️ No dealId available for status update');
        }
        
        // Find the selected offer data and store it for the success modal
        const selectedOffer = offers.find(offer => offer.id === offerId);
        setSelectedOfferData({
          ...selectedOffer,
          selectedAmount: amount
        });
        
        // Move to blank check upload step instead of showing success modal
        setPageStep(2);
      }
    } catch (error) {
      console.error('❌ Error selecting offer:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setError(`Failed to select offer: ${error.response.data.message}`);
      } else {
        setError('Failed to select offer. Please try again or contact support.');
      }
    } finally {
      setSelectLoading(prev => ({ ...prev, [offerId]: false }));
    }
  };

  // Get the minimum and maximum amounts for validation.
  // Use gross advance amounts for limits (same approach as modal hook):
  // gross = gross_advance_amount || (advance_amount + processing_fee)
  const getAmountLimits = (currentOffer, allOffers) => {
    // Helper to compute gross amount for an offer
    const grossOf = (offer) => {
      return (offer.gross_advance_amount || offer.grossAdvanceAmount || (Number(offer.advance_amount || 0) + Number(offer.processing_fee || 0)));
    };

    // Clone and sort by gross to avoid mutating original array
    const sortedOffers = [...allOffers].sort((a, b) => grossOf(a) - grossOf(b));
    const currentIndex = sortedOffers.findIndex(offer => offer.id === currentOffer.id);

    // Minimum gross is configurable (OFFER_CONFIG.MIN_ADVANCE_AMOUNT)
    const minGross = OFFER_CONFIG.MIN_ADVANCE_AMOUNT;

    // Maximum gross: if last offer, cannot increase beyond its original gross
    // otherwise, can increase up to one step before next offer's gross
    let maxGross;
    const currentGross = grossOf(currentOffer);
    if (currentIndex === sortedOffers.length - 1) {
      maxGross = currentGross;
    } else {
      const nextGross = grossOf(sortedOffers[currentIndex + 1]);
      maxGross = nextGross - OFFER_CONFIG.AMOUNT_STEP;
    }

    if (maxGross < minGross) maxGross = minGross;

    // Return limits as gross amounts and also compute equivalent advance amounts
    const minAdvance = minGross - (currentOffer.processing_fee || 0);
    const maxAdvance = maxGross - (currentOffer.processing_fee || 0);

    return { minGross, maxGross, minAdvance, maxAdvance };
  };

  // Validate if amount is within allowed range
  // Validate a proposed advance amount by converting to gross and checking step and bounds
  const isValidAmount = (offer, proposedAdvanceAmount, currentOffers = offers) => {
    const { minGross, maxGross } = getAmountLimits(offer, currentOffers);
    const fee = Number(offer.processing_fee || 0);
    const proposedGross = Number(proposedAdvanceAmount || 0) + fee;

    // Within gross bounds
    if (proposedGross < minGross || proposedGross > maxGross) return false;

    // Must be aligned to step on gross amount
    return (proposedGross % OFFER_CONFIG.AMOUNT_STEP) === 0;
  };

  // Handle increment/decrement - based on PreApprovedTab logic
  // Adjusts GROSS ADVANCE AMOUNT and recalculates all other fields
  const adjustAmount = (offerId, direction) => {
    const offer = offers.find(o => o.id === offerId);
    if (!offer) return;
    
    const step = OFFER_CONFIG.AMOUNT_STEP;
    const originalGrossAmount = offer.gross_advance_amount || 
                               offer.grossAdvanceAmount || 
                               (Number(offer.advance_amount || 0) + Number(offer.processing_fee || 0));
    const currentGross = Number(editingAmounts[offerId] || originalGrossAmount);
    const minGross = OFFER_CONFIG.MIN_ADVANCE_AMOUNT;
    
    let newGrossAmount = currentGross;
    if (direction === 'increment') {
      newGrossAmount = Math.min(currentGross + step, originalGrossAmount);
    } else {
      newGrossAmount = Math.max(currentGross - step, minGross);
    }
    
    // Only update if the amount is valid and different from current
    if (newGrossAmount !== currentGross && 
        newGrossAmount >= minGross && 
        newGrossAmount <= originalGrossAmount &&
        newGrossAmount % step === 0) {
      handleAmountChange(offerId, newGrossAmount);
    }
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    return formatNumber(amount);
  };

  // Handle closing the success modal and window
  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSelectedOfferData(null);
    // Close the current window/tab
    window.close();
  };

  // Función para manejar la subida del blank check
  const handleBlankCheckUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!dealId) {
      console.error('Deal ID is required for file upload');
      setBlankCheckUploadError('Deal information is missing');
      return;
    }

    try {
      setUploadingBlankCheck(true);
      setBlankCheckUploadError(null);

      // Get deal data to access legalName and publicId (EXT)
      console.log('🔍 Getting deal data for custom file name...');
      const dealResponse = await extDealService.getDealByPrivateIdExt(dealId);
      const dealData = dealResponse.data;

      // Get file extension
      const fileExtension = file.name.split('.').pop();
      
      // Create custom file name: legalName - publicId - blank-check
      const legalName = dealData.legalName || dealData.company_name || 'Unknown';
      const publicId = dealData.publicId || dealData.id || 'Unknown';
      const customFileName = `${legalName} - ${publicId} - blank-check.${fileExtension}`;
      
      console.log('📄 Uploading blank check with custom name:', customFileName);

      // Create a new File object with the custom name
      const renamedFile = new File([file], customFileName, { type: file.type });

      // Upload document using extDocumentService
      const { documentId, details } = await extDocumentService.uploadDocumentExt({
        file: renamedFile,
        category: 'blank_check',
        originType: 'blank_check',
        originId: dealId,
        description: `Blank check for deal`,
        tags: ['blank_check', 'contract'],
        onUploadProgress: (progress) => {
          console.log('Upload progress:', progress.percent + '%');
        }
      });

      console.log('✅ Blank check uploaded successfully:', { documentId, details });

      // Update state
      setBlankCheckFile({
        fileName: file.name,
        documentId: documentId,
        details: details
      });

      // Update has-blank-check to true (EXT)
      await updateHasBlankCheck(true);

      // Show success modal instead of closing
      setShowSuccessModal(true);

    } catch (error) {
      console.error('❌ Error uploading blank check:', error);
      setBlankCheckUploadError(error.message || 'Failed to upload blank check');
    } finally {
      setUploadingBlankCheck(false);
    }
  };

  // Función para actualizar has-blank-check (EXT)
  const updateHasBlankCheck = async (hasBlankCheck) => {
    try {
      if (!dealId) {
        throw new Error('Deal ID is required');
      }

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      console.log(`🔄 Updating has-blank-check to ${hasBlankCheck}...`);

      await extDealService.updateHasBlankCheckExt(dealId, hasBlankCheck);

      console.log(`✅ has-blank-check updated to ${hasBlankCheck} successfully`);

    } catch (error) {
      console.error('❌ Error updating has-blank-check:', error);
      throw error;
    }
  };

  // Función para saltar la subida del blank check
  const handleSkipBlankCheck = async () => {
    try {
      setUploadingBlankCheck(true);
      setBlankCheckUploadError(null);

      // Update has-blank-check to false (EXT)
      await updateHasBlankCheck(false);

      // Show success modal instead of closing
      setShowSuccessModal(true);

    } catch (error) {
      console.error('❌ Error skipping blank check:', error);
      setBlankCheckUploadError(error.message || 'Failed to skip blank check');
    } finally {
      setUploadingBlankCheck(false);
    }
  };

  // Render blank check upload step (Step 2)
  if (pageStep === 2) {
    return (
      <>
        <Container className="mt-5">
          <Row className="justify-content-center">
            <Col md="8" lg="6">
              <Card className="shadow-lg border-0" style={{ borderRadius: '1rem' }}>
                <CardBody className="text-center p-5">
                  {/* Warning Icon and Message */}
                  <div className="mb-4">
                    <div 
                      className="icon icon-shape bg-gradient-warning rounded-circle shadow mx-auto mb-3"
                      style={{ 
                        width: '80px', 
                        height: '80px',
                        background: 'linear-gradient(135deg, #fb6340, #fbb140)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="ni ni-bell-55 text-white" style={{ fontSize: '2.5rem' }} />
                    </div>
                    
                    <h2 className="text-warning mb-3" style={{ fontWeight: '700', fontSize: '1.5rem' }}>
                      BLANK CHECK IS REQUIRED BEFORE CONTRACT FUNDING CAN OCCUR
                    </h2>
                    
                    <p className="text-muted mb-4" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
                      Please upload a blank check to continue with the contract funding process.
                    </p>
                  </div>

                  {/* Error Message */}
                  {blankCheckUploadError && (
                    <Alert color="danger" className="mb-4">
                      <i className="ni ni-support-16 mr-2" />
                      {blankCheckUploadError}
                    </Alert>
                  )}

                  {/* Success Message */}
                  {blankCheckFile && (
                    <Alert color="success" className="mb-4">
                      <i className="ni ni-check-bold mr-2" />
                      File uploaded successfully: {blankCheckFile.fileName}
                    </Alert>
                  )}

                  {/* Upload Section */}
                  <div className="mb-4">
                    <input
                      type="file"
                      id="blankCheckInput"
                      onChange={handleBlankCheckUpload}
                      style={{ display: 'none' }}
                      aria-label="Upload blank check"
                      disabled={uploadingBlankCheck}
                      accept=".pdf,.png,.jpg,.jpeg,.gif"
                    />
                    <Button
                      color="primary"
                      size="lg"
                      onClick={() => document.getElementById('blankCheckInput').click()}
                      disabled={uploadingBlankCheck}
                      className="px-5"
                      style={{ 
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        padding: '0.75rem 2rem'
                      }}
                    >
                      {uploadingBlankCheck ? (
                        <>
                          <Spinner size="sm" className="mr-2" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="ni ni-cloud-upload-96 mr-2" />
                          Upload Blank Check
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Divider */}
                  <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                  {/* Skip Button - Small and almost imperceptible */}
                  <div>
                    <Button
                      color="link"
                      size="sm"
                      onClick={handleSkipBlankCheck}
                      disabled={uploadingBlankCheck}
                      className="text-muted"
                      style={{ 
                        fontSize: '0.75rem',
                        textDecoration: 'none',
                        opacity: 0.5
                      }}
                    >
                      Skip
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* Success Modal - Shows after blank check handling */}
        {showSuccessModal && selectedOfferData && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg border-0" style={{ borderRadius: '1rem' }}>
                <div className="modal-body text-center p-5">
                  {/* Success Icon */}
                  <div className="mb-4">
                    <div 
                      className="icon icon-shape bg-gradient-success rounded-circle shadow mx-auto"
                      style={{ 
                        width: '80px', 
                        height: '80px',
                        background: 'linear-gradient(135deg, #2dce89, #2dcecc)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="ni ni-check-bold text-white" style={{ fontSize: '2.5rem' }} />
                    </div>
                  </div>

                  {/* Success Title */}
                  <h2 className="text-dark mb-3" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                    Your offer has been selected successfully!
                  </h2>

                  {/* Success Message */}
                  <p className="text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                    Your funding request for <strong>{formatCurrency(selectedOfferData.selectedAmount)}</strong> has been processed successfully. Your deal status has been updated to CONTRACT.
                  </p>

                  {/* Offer Details */}
                  <div className="bg-light rounded p-3 mb-4">
                    <div className="row text-center">
                      <div className="col-6">
                        <h6 className="text-muted mb-1">Selected Amount</h6>
                        <h4 className="text-primary mb-0">{formatCurrency(selectedOfferData.selectedAmount)}</h4>
                      </div>
                      <div className="col-6">
                        <h6 className="text-muted mb-1">Term</h6>
                        <h4 className="text-dark mb-0">{selectedOfferData.term_duration_months || 'N/A'} months</h4>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                  {/* Close Button */}
                  <Button 
                    color="primary" 
                    size="lg"
                    className="px-5"
                    onClick={handleCloseModal}
                    style={{ 
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      padding: '0.75rem 2rem'
                    }}
                  >
                    <i className="ni ni-check-bold mr-2" />
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // Render offers state (after successful token exchange) - Step 1
  if (!exchangeLoading && !loading && offers.length > 0 && pageStep === 1) {
    
    return (
      <Container className="mt-4" fluid>
        <Row className="justify-content-center">
          <Col xl="10" lg="12">
            <Card className="shadow-lg border-0">
              <CardHeader className="text-white border-0" style={{ background: 'linear-gradient(to right, #00C6FF, #0072FF)' }}>
                <Row className="align-items-center">
                  <Col>
                    <div className="d-flex align-items-center">
                      <div className="icon icon-shape bg-white rounded-circle shadow mr-3" style={{ color: '#0072FF' }}>
                        <i className="ni ni-money-coins" />
                      </div>
                      <div>
                        <h3 className="mb-0 text-white">NLF Funding Offers</h3>
                        <p className="text-white opacity-8 mb-0">Select your preferred funding option</p>
                      </div>
                    </div>
                  </Col>
                  <Col xs="auto">
                    {timeRemaining ? (
                      <span className={`badge px-3 py-2 ${timeRemaining.minutes < 5 ? 'badge-warning' : 'badge-info'}`}>
                        <i className="ni ni-watch-time mr-1" />
                        {timeRemaining.minutes.toString().padStart(2, '0')}:{timeRemaining.seconds.toString().padStart(2, '0')} remaining
                      </span>
                    ) : (
                      <span className="badge badge-danger px-3 py-2">
                        <i className="ni ni-watch-time mr-1" /> Session Expired
                      </span>
                    )}
                  </Col>
                </Row>
              </CardHeader>
              
              <CardBody className="p-4">
                {/* Approval message */}
                <div className="text-center mb-4">
                  <h4 className="text-success mb-2">
                    <i className="ni ni-check-bold mr-2" />
                    Your application has been approved!
                  </h4>
                  <p className="text-muted mb-0">Please select your preferred funding option below</p>
                </div>

                {/* Offers Table - Completely Rewritten */}
                <div className="table-responsive">
                  <table className="table table-hover table-sm" id="offers-table" style={{ fontSize: '0.85rem', marginBottom: 0 }}>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: '12%', textAlign: 'center' }}>Term</th>
                        <th style={{ width: '14%', textAlign: 'center' }}>Advance amount</th>
                        <th style={{ width: '16%', textAlign: 'center' }}>Gross advance amount</th>
                        <th style={{ width: '12%', textAlign: 'center' }}>Payback</th>
                        <th style={{ width: '8%', textAlign: 'center' }}>Factor</th>
                        <th style={{ width: '12%', textAlign: 'center' }}>Monthly payment</th>
                        <th style={{ width: '10%', textAlign: 'center' }}>Fee</th>
                        <th style={{ width: '16%', textAlign: 'center' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.map((offer, index) => {
                        // Cálculo en tiempo real - basado en GROSS ADVANCE AMOUNT editado
                        const fee = Number(offer.processing_fee || 0);
                        const factor = Number(offer.factor || 0);
                        const termDuration = offer.term_duration_months || 1;
                        
                        // Get original gross amount
                        const originalGrossAmount = offer.gross_advance_amount || 
                                                   offer.grossAdvanceAmount || 
                                                   (Number(offer.advance_amount || 0) + fee);
                        
                        // Get current edited gross amount
                        const currentGross = Number(editingAmounts[offer.id] || originalGrossAmount);
                        
                        // Recalculate all fields based on current gross amount
                        const currentAdvance = Math.max(0, currentGross - fee); // advance = gross - fee
                        const netPayback = currentGross * factor; // payback = gross * factor
                        const monthlyPayment = netPayback / termDuration; // monthly = payback / term
                        
                        const minGross = OFFER_CONFIG.MIN_ADVANCE_AMOUNT;
                        
                        // Can decrement if current amount is greater than minimum
                        const canDecrement = currentGross > minGross && !Object.values(selectLoading).some(l => l);
                        // Can increment if current amount is less than original (only after decrementing)
                        const canIncrement = currentGross < originalGrossAmount && !Object.values(selectLoading).some(l => l);
                        
                        return (
                          <tr key={`offer-${offer.id}-${index}`}>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <strong>{termDuration} months</strong>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <strong>{formatCurrency(currentAdvance)}</strong>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <div className="d-flex align-items-center justify-content-center">
                                <Button
                                  color="outline-secondary"
                                  size="sm"
                                  className="mr-1"
                                  style={{ padding: '0.25rem 0.4rem', fontSize: '0.75rem' }}
                                  onClick={() => adjustAmount(offer.id, 'decrement')}
                                  disabled={!canDecrement}
                                  aria-label="decrement"
                                >
                                  −
                                </Button>
                                <span className="font-weight-bold mx-1" style={{ minWidth: '60px' }}>
                                  {formatCurrency(currentGross)}
                                </span>
                                <Button
                                  color="outline-secondary"
                                  size="sm"
                                  className="ml-1"
                                  style={{ padding: '0.25rem 0.4rem', fontSize: '0.75rem' }}
                                  onClick={() => adjustAmount(offer.id, 'increment')}
                                  disabled={!canIncrement}
                                  aria-label="increment"
                                >
                                  +
                                </Button>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <strong>{formatCurrency(netPayback)}</strong>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <strong>{factor ? factor.toFixed(2) : 'N/A'}</strong>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <strong>{formatCurrency(monthlyPayment)}</strong>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <strong>{formatCurrency(fee)}</strong>
                            </td>
                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                              <Button 
                                color="primary" 
                                size="sm"
                                onClick={() => handleSelectOffer(offer.id)}
                                disabled={Object.values(selectLoading).some(loading => loading)}
                                style={{ padding: '0.375rem 0.75rem', fontSize: '0.8rem' }}
                              >
                                {selectLoading[offer.id] ? (
                                  <>
                                    <Spinner size="sm" className="mr-1" />
                                    Selecting...
                                  </>
                                ) : (
                                  'Select'
                                )}
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Funding Conditions */}
                <div className="mt-4">
                  <h5 className="text-muted mb-3">Funding Conditions:</h5>
                  <ul className="list-unstyled">
                    <li className="mb-2">
                      <i className="ni ni-check-bold text-success mr-2" />
                      Void check
                    </li>
                    <li className="mb-2">
                      <i className="ni ni-check-bold text-success mr-2" />
                      Fully executed contracts
                    </li>
                  </ul>
                </div>

                {/* Addendum Checkbox */}
                <div className="mt-4 d-flex align-items-center justify-content-center">
                  <div className="custom-control custom-checkbox">
                    <input
                      type="checkbox"
                      className="custom-control-input"
                      id="addendumCheckbox"
                      checked={addendumChecked}
                      onChange={(e) => setAddendumChecked(e.target.checked)}
                      disabled={Object.values(selectLoading).some(loading => loading)}
                    />
                    <label className="custom-control-label" htmlFor="addendumCheckbox">
                      <span className="text-dark font-weight-bold">Addendum</span>
                    </label>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        
        {/* Success Modal */}
        {showSuccessModal && selectedOfferData && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content shadow-lg border-0" style={{ borderRadius: '1rem' }}>
                <div className="modal-body text-center p-5">
                  {/* Success Icon */}
                  <div className="mb-4">
                    <div 
                      className="icon icon-shape bg-gradient-success rounded-circle shadow mx-auto"
                      style={{ 
                        width: '80px', 
                        height: '80px',
                        background: 'linear-gradient(135deg, #2dce89, #2dcecc)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="ni ni-check-bold text-white" style={{ fontSize: '2.5rem' }} />
                    </div>
                  </div>

                  {/* Success Title */}
                  <h2 className="text-dark mb-3" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                    Your offer has been selected successfully!
                  </h2>

                  {/* Success Message */}
                  <p className="text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                    Your funding request for <strong>{formatCurrency(selectedOfferData.selectedAmount)}</strong> has been processed successfully. Your deal status has been updated to CONTRACT.
                  </p>

                  {/* Offer Details */}
                  <div className="bg-light rounded p-3 mb-4">
                    <div className="row text-center">
                      <div className="col-6">
                        <h6 className="text-muted mb-1">Selected Amount</h6>
                        <h4 className="text-primary mb-0">{formatCurrency(selectedOfferData.selectedAmount)}</h4>
                      </div>
                      <div className="col-6">
                        <h6 className="text-muted mb-1">Term</h6>
                        <h4 className="text-dark mb-0">{selectedOfferData.term_duration_months || 'N/A'} months</h4>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                  {/* Close Button */}
                  <Button 
                    color="primary" 
                    size="lg"
                    className="px-5"
                    onClick={handleCloseModal}
                    style={{ 
                      borderRadius: '0.5rem',
                      fontWeight: '600',
                      padding: '0.75rem 2rem'
                    }}
                  >
                    <i className="ni ni-check-bold mr-2" />
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md="8" lg="6">
            <Card className="shadow-lg border-0" style={{ borderRadius: '1rem' }}>
              <CardBody className="text-center p-5">
                {/* Error Icon */}
                <div className="mb-4">
                  <div 
                    className="icon icon-shape bg-gradient-danger rounded-circle shadow mx-auto"
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="ni ni-fat-remove text-white" style={{ fontSize: '2.5rem' }} />
                  </div>
                </div>

                {/* Error Title */}
                <h2 className="text-danger mb-3" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                Your session has expired
                </h2>

                {/* Error Message */}
                <p className="text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                  {error}
                </p>

                {/* Divider */}
                <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                {/* Support Information */}
                <div className="p-4 mb-4">
                  <h5 className="text-dark mb-2" style={{ fontSize: '1rem', fontWeight: '600' }}>
                    <i className="ni ni-support-16 text-primary mr-2" />
                    Need Help?
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                    Please try refreshing the page or contact our support team if the issue persists.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render loading state
  if (!urlCleaned || exchangeLoading || loading) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md="6">
            <Card className="shadow">
              <CardBody className="text-center">
                <Spinner color="primary" className="mb-3" />
                <h4>
                  {!urlCleaned ? 'Processing Link...' : 
                   exchangeLoading ? 'Validating Access...' : 
                   'Loading Offers...'}
                </h4>
                <p className="text-muted">
                  {!urlCleaned ? 'Extracting token from URL...' : 
                   exchangeLoading ? 'Exchanging magic token for session...' : 
                   'Fetching your available offers...'}
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render error state if no token found
  if (!magicToken) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md="8" lg="6">
            <Card className="shadow-lg border-0" style={{ borderRadius: '1rem' }}>
              <CardBody className="text-center p-5">
                {/* Error Icon */}
                <div className="mb-4">
                  <div 
                    className="icon icon-shape bg-gradient-danger rounded-circle shadow mx-auto"
                    style={{ 
                      width: '80px', 
                      height: '80px',
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a52)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <i className="ni ni-fat-remove text-white" style={{ fontSize: '2.5rem' }} />
                  </div>
                </div>

                {/* Error Title */}
                <h2 className="text-danger mb-3" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                  Invalid Access Link
                </h2>

                {/* Error Message */}
                <p className="text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                  This link is invalid or has expired. Please check your email and try again with a fresh link.
                </p>

                {/* Divider */}
                <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

                {/* Support Information */}
                <div className="p-4 mb-4">
                  <h5 className="text-dark mb-2" style={{ fontSize: '1rem', fontWeight: '600' }}>
                    <i className="ni ni-support-16 text-primary mr-2" />
                    Need Help?
                  </h5>
                  <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                    If you continue to experience issues, please contact our support team for assistance.
                  </p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Render no offers state
  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md="8" lg="6">
          <Card className="shadow-lg border-0" style={{ borderRadius: '1rem' }}>
            <CardBody className="text-center p-5">
              {/* Info Icon */}
              <div className="mb-4">
                <div 
                  className="icon icon-shape bg-gradient-info rounded-circle shadow mx-auto"
                  style={{ 
                    width: '80px', 
                    height: '80px',
                    background: 'linear-gradient(135deg, #11cdef, #1171ef)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="ni ni-bullet-list-67 text-white" style={{ fontSize: '2.5rem' }} />
                </div>
              </div>

              {/* Info Title */}
              <h2 className="text-info mb-3" style={{ fontWeight: '600', fontSize: '1.75rem' }}>
                No Offers Available
              </h2>

              {/* Info Message */}
              <p className="text-muted mb-4" style={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                No funding offers are currently available for your account. This may be temporary.
              </p>

              {/* Divider */}
              <hr className="my-4" style={{ borderColor: '#e9ecef' }} />

              {/* Support Information */}
              <div className="p-4 mb-4">
                <h5 className="text-dark mb-2" style={{ fontSize: '1rem', fontWeight: '600' }}>
                  <i className="ni ni-support-16 text-primary mr-2" />
                  Need More Information?
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: '0.95rem' }}>
                  Please contact our support team for more information about available funding options.
                </p>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default OffersSelect;
